// react
import React from 'react';

// components
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { Icon } from '@/components/atoms/Icon/Icon';
import { resolveIcon } from '@/components/atoms/Icon/resolveIcon';

// mui
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface FormCompleteItem {
  icon: string;
  text: string;
}

const incompleteSteps = [
  { text: 'Please complete all of the questions in the form' },
  {
    text: 'Head back using the "Back" button, or use the Sections to fill in the blanks',
  },
  { text: 'Return to submit this form once complete' },
];

export interface FormCompletePageProps {
  onBack: () => void;
  title: string;
  isComplete: boolean;
  items: FormCompleteItem[];
  onSubmit: () => void;
  submitLabel?: string;
  isSubmitDisabled?: boolean;
  onPrevious?: () => void;
}

/**
 * FormCompletePage shows form completion status and next steps.
 * It uses ModalPageTemplate + PageTemplate for consistent navigation.
 */
export const FormCompletePage = ({
  onBack,
  title,
  isComplete,
  items,
  onSubmit,
  submitLabel = 'Submit Form',
  isSubmitDisabled,
  onPrevious,
}: FormCompletePageProps) => {
  const theme = useTheme();

  return (
    <ModalPageTemplate
      onBack={onBack}
      footerSlot={
        <Box
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack
            spacing={2}
            sx={{
              maxWidth: 'sm',
              mx: 'auto',
            }}
          >
            <Button
              variant='contained'
              size='large'
              fullWidth
              onClick={onSubmit}
              disabled={isSubmitDisabled}
            >
              {submitLabel}
            </Button>
            {onPrevious && (
              <Button
                variant='outlined'
                size='large'
                fullWidth
                onClick={onPrevious}
              >
                Previous
              </Button>
            )}
          </Stack>
        </Box>
      }
    >
      <PageTemplate
        maxWidth='sm'
        headerSlot={<PageHeader title={title} />}
        contentSlot={
          <Stack spacing={3}>
            <Stack
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Icon icon='clipboard-check' fontSize={50} />
              <Typography variant='h2' align='center'>
                {title} {isComplete ? 'Ready For Review' : 'Needs Completing'}
              </Typography>
            </Stack>

            <Card elevation={0}>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant='subtitle1'>Your next steps</Typography>
                  {isComplete
                    ? items.map((item, index) => (
                        <Stack
                          key={index}
                          direction='row'
                          spacing={2}
                          sx={{
                            alignItems: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              width: 40,
                              minWidth: 40,
                            }}
                          >
                            <Icon icon={resolveIcon(item.icon)} fontSize={24} />
                          </Box>
                          <Typography variant='body2'>{item.text}</Typography>
                        </Stack>
                      ))
                    : incompleteSteps.map((step, index) => (
                        <Stack
                          key={index}
                          direction='row'
                          spacing={2}
                          sx={{
                            alignItems: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              width: 40,
                              minWidth: 40,
                            }}
                          >
                            <Typography variant='subtitle2'>
                              {index + 1}.
                            </Typography>
                          </Box>
                          <Typography variant='body2'>{step.text}</Typography>
                        </Stack>
                      ))}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        }
      />
    </ModalPageTemplate>
  );
};

FormCompletePage.displayName = 'FormCompletePage';
export default FormCompletePage;
